/**
 * Global ecosystem wallet
 * ---------------------------------------------------------------------------
 * Single source of truth for the simulated Web3 wallet used across the SFN
 * landing page, the MEAL4 / MEAL2 direct sale, the VillagePando dashboard,
 * the internal DEX (EcosystemSwap), and every asset contribution panel.
 *
 * All modules read the same balances from this provider, so a swap or a
 * contribution on ONE page updates the numbers on every other page instantly.
 *
 * Persisted to localStorage under `sfn.wallet.v1` so navigation between
 * routes keeps the user "connected" and preserves the interlocking-gear UX.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Loader2, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import {
  SOLANA_CLUSTER,
  SOLANA_CLUSTER_LABEL,
  clusterFromEndpoint,
  type SolanaCluster,
} from "@/lib/solana-rpc";

/* ────────────────────────────────────────────────────────────────── */
/* Tokens + pricing (global constants — do not fork per component)   */
/* ────────────────────────────────────────────────────────────────── */

export type TokenSym =
  | "USDC"
  | "SOL"
  | "MEAL2"
  | "MEAL3"
  | "MEAL4"
  | "MEAL5"
  | "SOLAR-PANDO"
  | "AQUA-PANDO"
  | "PLANTY-PANDO"
  | "VILLAGE-PANDO";

/**
 * Global THB↔USD reference used by every "Physical vs Financial" toggle.
 * Pinned to 30 THB/USD to keep narrative parity with the "30 THB per meal = $1"
 * marketing baseline. Do not float this without updating landing/hero copy.
 */
export const THB_PER_USD = 30;

/**
 * Tokens reserved on-chain but not yet issued.
 * quoteSwap() throws if any is used as `from` or `to` — the UI must gate them
 * behind a "Coming Soon" badge and disable swap paths.
 */
export const UNISSUED_TOKENS = new Set<TokenSym>(["MEAL3", "MEAL5"]);

/** Canonical USD reference price per token (mock AMM matrix). */
export const USD_PER_TOKEN: Record<TokenSym, number> = {
  USDC: 1,
  SOL: 150,
  MEAL2: 1,
  MEAL3: NaN, // reserved — Coming 2027
  MEAL4: 0.1,
  MEAL5: NaN, // reserved — Coming 2027
  "SOLAR-PANDO": 0.1,
  "AQUA-PANDO": 0.08,
  "PLANTY-PANDO": 0.05,
  "VILLAGE-PANDO": 1, // 1:1 USDC-backed voucher — NOT market-traded
};

/**
 * VillagePando is a 1:1 USDC-backed voucher (RWA claim), not a market-priced
 * governance token. The swap UI must render a "1:1 USDC-backed voucher" badge
 * wherever this symbol appears and disable slippage/synergy math.
 */
export const PEGGED_TOKENS = new Set<TokenSym>(["USDC", "VILLAGE-PANDO"]);

const UTILITY = new Set<TokenSym>(["MEAL2", "MEAL4"]);
const YIELD = new Set<TokenSym>([
  "SOLAR-PANDO",
  "AQUA-PANDO",
  "PLANTY-PANDO",
]);

/**
 * Ecosystem synergy bonus. Symmetric across the yield ↔ utility corridor so
 * the circular economy closes in both directions:
 *   YIELD  → UTILITY  = +5%  (feed the kitchens)
 *   UTILITY → YIELD    = +5%  (recycle meals into RWA yield)
 * VillagePando vouchers earn a 2% redemption discount whenever a swap
 * targets the pegged voucher (see PEGGED_TOKENS gating in quoteSwap).
 */
export function synergyBonus(from: TokenSym, to: TokenSym): number {
  if (YIELD.has(from) && UTILITY.has(to)) return 0.05;
  if (UTILITY.has(from) && YIELD.has(to)) return 0.05;
  if (to === "VILLAGE-PANDO" && !PEGGED_TOKENS.has(from)) return 0.02;
  return 0;
}

/** Deterministic AMM quote used everywhere the ecosystem needs a swap price. */
export function quoteSwap(from: TokenSym, to: TokenSym, amount: number) {
  if (from === to || amount <= 0 || !isFinite(amount)) {
    return { out: 0, rate: 0, bonus: 0, usdIn: 0, slippage: 0, fee: 0 };
  }
  if (UNISSUED_TOKENS.has(from) || UNISSUED_TOKENS.has(to)) {
    throw new Error(`token-not-yet-issued:${UNISSUED_TOKENS.has(from) ? from : to}`);
  }
  const priceIn = USD_PER_TOKEN[from];
  const priceOut = USD_PER_TOKEN[to];
  if (!isFinite(priceIn) || !isFinite(priceOut)) {
    throw new Error("price-unavailable");
  }
  const usdIn = amount * priceIn;
  // Pegged vouchers bypass slippage and synergy — 1:1 backing implies a fixed rail.
  const pegged = PEGGED_TOKENS.has(from) && PEGGED_TOKENS.has(to);
  const slippage = pegged ? 0 : 0.005; // 0.5%
  const fee = 0.008; // flat $0.008 network fee
  const bonus = pegged ? 0 : synergyBonus(from, to);
  const usdOut = Math.max(0, usdIn * (1 - slippage) - fee) * (1 + bonus);
  const out = usdOut / priceOut;
  return { out, rate: amount > 0 ? out / amount : 0, bonus, usdIn, slippage, fee };
}

/* ────────────────────────────────────────────────────────────────── */
/* Wallet state                                                       */
/* ────────────────────────────────────────────────────────────────── */

export type Balances = Record<TokenSym, number>;

const DEFAULT_BALANCES: Balances = {
  USDC: 500,
  SOL: 3.2,
  MEAL2: 1_240.5,
  MEAL3: 0,
  MEAL4: 3_820,
  MEAL5: 0,
  "SOLAR-PANDO": 5_400,
  "AQUA-PANDO": 3_100,
  "PLANTY-PANDO": 12_800,
  "VILLAGE-PANDO": 62.4,
};

export type Provider = "phantom" | "solflare" | "backpack";

type Persisted = { address: string; balances: Balances; provider?: Provider };

const STORAGE_KEY = "sfn.wallet.v1";

function readPersisted(): Persisted | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    if (!parsed?.address || !parsed?.balances) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(p: Persisted | null) {
  if (typeof window === "undefined") return;
  try {
    if (!p) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore quota */
  }
}

/* ── Real injected Solana wallet providers (Phantom / Solflare / Backpack) ── */

type SolanaProvider = {
  publicKey?: { toBase58?: () => string; toString: () => string } | null;
  isConnected?: boolean;
  connect: (opts?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey?: { toString: () => string } } | void>;
  disconnect: () => Promise<void>;
  on?: (event: string, cb: (...args: unknown[]) => void) => void;
  off?: (event: string, cb: (...args: unknown[]) => void) => void;
};

export const WALLET_LABEL: Record<Provider, string> = {
  phantom: "Phantom",
  solflare: "Solflare",
  backpack: "Backpack",
};

export const WALLET_INSTALL_URL: Record<Provider, string> = {
  phantom: "https://phantom.app/download",
  solflare: "https://solflare.com/download",
  backpack: "https://backpack.app/downloads",
};

/** Returns the injected provider for a wallet, or null when not installed. */
export function getInjectedProvider(provider: Provider): SolanaProvider | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, any>;
  switch (provider) {
    case "phantom":
      return (w.phantom?.solana?.isPhantom ? w.phantom.solana : w.solana?.isPhantom ? w.solana : null) ?? null;
    case "solflare":
      return (w.solflare?.isSolflare ? w.solflare : null) ?? null;
    case "backpack":
      return (w.backpack?.isBackpack ? w.backpack : w.xnft?.solana ?? null) ?? null;
    default:
      return null;
  }
}

function readPublicKey(p: SolanaProvider | null | undefined): string | null {
  const pk = p?.publicKey;
  if (!pk) return null;
  const v = typeof pk.toBase58 === "function" ? pk.toBase58() : pk.toString();
  return v && v.length >= 32 ? v : null;
}

/* ────────────────────────────────────────────────────────────────── */
/* Context                                                            */
/* ────────────────────────────────────────────────────────────────── */

type WalletCtx = {
  address: string | null;
  connected: boolean;
  connecting: boolean;
  balances: Balances;
  /** Which injected wallet is currently attached (null when disconnected). */
  provider: Provider | null;
  /** Display name of the connected wallet, e.g. "Phantom". */
  providerLabel: string | null;
  /** Solana cluster this app talks to (derived from the configured RPC). */
  cluster: SolanaCluster;
  clusterLabel: string;
  /** Cluster reported by the connected wallet (null when it can't be read). */
  walletCluster: SolanaCluster | null;
  /** True when the wallet is demonstrably on a different network than the app. */
  clusterMismatch: boolean;
  /** True while a disconnect is in flight. */
  disconnecting: boolean;
  /** Real wallet connection through the injected Solana provider. */
  connect: (provider?: Provider) => Promise<void>;
  disconnect: () => void;
  /** Increment a token balance (mint/receive). */
  credit: (sym: TokenSym, amount: number) => void;
  /** Decrement a token balance (send/pay). */
  debit: (sym: TokenSym, amount: number) => void;
  /**
   * Full simulated swap sequence:
   *   1. Requires connected wallet.
   *   2. 2-second "routing through Pando smart contracts" delay.
   *   3. Debits `from` and credits `to` (with synergy bonus).
   *   4. Returns a fake Polygon-style tx hash.
   */
  swap: (from: TokenSym, to: TokenSym, amount: number) => Promise<{
    hash: string;
    out: number;
    bonus: number;
  }>;
  /** Show / hide the unified connect modal. */
  openConnect: () => void;
  closeConnect: () => void;
};

const Ctx = createContext<WalletCtx | null>(null);

export function useWallet(): WalletCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useWallet must be used inside <WalletProvider>");
  return ctx;
}

/** Optional variant — returns null when the provider is absent (SSR safe). */
export function useOptionalWallet(): WalletCtx | null {
  return useContext(Ctx);
}

/* ────────────────────────────────────────────────────────────────── */

/** Best-effort audit-trail entry for wallet lifecycle events. */
async function auditWallet(
  event: "wallet_connect" | "wallet_disconnect",
  wallet: string | null,
  detail: Record<string, string>,
): Promise<void> {
  try {
    const { reportAuditEvent } = await import("@/lib/audit.functions");
    await reportAuditEvent({ data: { event, wallet, detail } });
  } catch {
    /* auditing must never block the wallet UX */
  }
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const [address, setAddress] = useState<string | null>(null);
  const [balances, setBalances] = useState<Balances>(DEFAULT_BALANCES);
  const [connecting, setConnecting] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [activeProvider, setActiveProvider] = useState<Provider | null>(null);
  const [walletCluster, setWalletCluster] = useState<SolanaCluster | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Best-effort read of the network the connected wallet is pointing at.
  useEffect(() => {
    if (!activeProvider || !address) {
      setWalletCluster(null);
      return;
    }
    let cancelled = false;
    const read = async () => {
      const injected = getInjectedProvider(activeProvider) as
        | (SolanaProvider & Record<string, any>)
        | null;
      if (!injected) return;
      const candidates: unknown[] = [
        injected["network"],
        injected["chainId"],
        injected["cluster"],
        injected["connection"]?.rpcEndpoint,
        injected["_network"],
      ];
      let found: SolanaCluster | null = null;
      for (const c of candidates) {
        found = clusterFromEndpoint(typeof c === "string" ? c : null);
        if (found) break;
      }
      if (!cancelled) setWalletCluster(found);
    };
    void read();
    return () => {
      cancelled = true;
    };
  }, [activeProvider, address]);

  const clusterMismatch = !!address && !!walletCluster && walletCluster !== SOLANA_CLUSTER;

  // Hydrate balances + silently re-attach to a previously trusted wallet.
  useEffect(() => {
    const p = readPersisted();
    if (!p) return;
    setBalances({ ...DEFAULT_BALANCES, ...p.balances });
    const prov = p.provider ?? "phantom";
    const injected = getInjectedProvider(prov);
    if (!injected) return;
    void (async () => {
      try {
        await injected.connect({ onlyIfTrusted: true });
        const key = readPublicKey(injected);
        if (key) {
          setActiveProvider(prov);
          setAddress(key);
        }
      } catch {
        /* wallet locked or not trusted — stay disconnected */
      }
    })();
  }, []);

  // Mirror wallet-side account switches / disconnects into app state.
  useEffect(() => {
    if (!activeProvider) return;
    const injected = getInjectedProvider(activeProvider);
    if (!injected?.on) return;
    const onAccountChanged = () => {
      const key = readPublicKey(injected);
      if (key) setAddress(key);
      else {
        setAddress(null);
        setActiveProvider(null);
        writePersisted(null);
      }
    };
    const onDisconnect = () => {
      setAddress(null);
      setActiveProvider(null);
      writePersisted(null);
    };
    injected.on("accountChanged", onAccountChanged);
    injected.on("disconnect", onDisconnect);
    return () => {
      injected.off?.("accountChanged", onAccountChanged);
      injected.off?.("disconnect", onDisconnect);
    };
  }, [activeProvider]);

  // Persist on change (only when connected).
  useEffect(() => {
    if (address) writePersisted({ address, balances, provider: activeProvider ?? undefined });
  }, [address, balances, activeProvider]);


  const connect = useCallback(async (provider: Provider = "phantom") => {
    const label = WALLET_LABEL[provider];
    const injected = getInjectedProvider(provider);
    if (!injected) {
      const msg = `${label} not detected in this browser.`;
      setConnectError(`${msg} Install the extension, then reload and try again.`);
      toast.error(msg, {
        description: "Install the extension, then reload this page.",
        action: {
          label: `Get ${label}`,
          onClick: () => window.open(WALLET_INSTALL_URL[provider], "_blank", "noopener,noreferrer"),
        },
      });
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      await injected.connect();
      const key = readPublicKey(injected);
      if (!key) throw new Error("no-public-key");
      setActiveProvider(provider);
      setAddress(key);
      setConnectOpen(false);
      void auditWallet("wallet_connect", key, { provider });
      toast.success(`${label} connected`, {
        description: `${key.slice(0, 4)}…${key.slice(-4)} · ${SOLANA_CLUSTER_LABEL[SOLANA_CLUSTER]}`,
      });
    } catch (e) {
      const raw = (e as { message?: string })?.message ?? "";
      const rejected = /reject|denied|4001|user cancel/i.test(raw);
      const message = rejected
        ? "Connection request rejected"
        : `Could not connect to ${label}`;
      const description = rejected
        ? "Approve the request in your wallet to continue."
        : "Make sure the wallet is unlocked, then retry or pick another wallet.";
      setConnectError(`${message} — ${description}`);
      toast.error(message, {
        description,
        action: { label: "Retry", onClick: () => void connectRef.current?.(provider) },
      });
    } finally {
      setConnecting(false);
    }
  }, []);

  // Stable handle so toast actions can re-invoke the latest connect().
  const connectRef = useRef<typeof connect | null>(null);
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    const label = activeProvider ? WALLET_LABEL[activeProvider] : "Wallet";
    const injected = activeProvider ? getInjectedProvider(activeProvider) : null;
    setDisconnecting(true);
    void Promise.resolve(injected?.disconnect?.())
      .catch(() => undefined)
      .finally(() => setDisconnecting(false));
    void auditWallet("wallet_disconnect", address, { provider: activeProvider ?? "unknown" });
    setActiveProvider(null);
    setAddress(null);
    setWalletCluster(null);
    writePersisted(null);
    toast(`${label} disconnected`, { description: "You can reconnect or switch wallets anytime." });
  }, [activeProvider, address]);

  const credit = useCallback((sym: TokenSym, amount: number) => {
    if (!isFinite(amount) || amount <= 0) return;
    setBalances((b) => ({ ...b, [sym]: (b[sym] ?? 0) + amount }));
  }, []);

  const debit = useCallback((sym: TokenSym, amount: number) => {
    if (!isFinite(amount) || amount <= 0) return;
    setBalances((b) => ({ ...b, [sym]: Math.max(0, (b[sym] ?? 0) - amount) }));
  }, []);

  const swap = useCallback<WalletCtx["swap"]>(
    async (from, to, amount) => {
      if (!address) throw new Error("wallet-not-connected");
      if (from === to || amount <= 0) throw new Error("invalid-swap");
      // Simulated on-chain routing latency.
      await new Promise((r) => setTimeout(r, 2000));
      const { out, bonus } = quoteSwap(from, to, amount);
      setBalances((b) => ({
        ...b,
        [from]: Math.max(0, (b[from] ?? 0) - amount),
        [to]: (b[to] ?? 0) + out,
      }));
      const hash =
        "0x" +
        Array.from({ length: 40 }, () =>
          Math.floor(Math.random() * 16).toString(16),
        ).join("");
      return { hash, out, bonus };
    },
    [address],
  );

  const value = useMemo<WalletCtx>(
    () => ({
      address,
      connected: !!address,
      connecting,
      balances,
      provider: activeProvider,
      providerLabel: activeProvider ? WALLET_LABEL[activeProvider] : null,
      cluster: SOLANA_CLUSTER,
      clusterLabel: SOLANA_CLUSTER_LABEL[SOLANA_CLUSTER],
      walletCluster,
      clusterMismatch,
      disconnecting,
      connect,
      disconnect,
      credit,
      debit,
      swap,
      openConnect: () => setConnectOpen(true),
      closeConnect: () => setConnectOpen(false),
    }),
    [
      address,
      activeProvider,
      connecting,
      disconnecting,
      walletCluster,
      clusterMismatch,
      balances,
      connect,
      disconnect,
      credit,
      debit,
      swap,
    ],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {clusterMismatch && walletCluster && (
        <ClusterMismatchBanner
          walletCluster={walletCluster}
          onSwitch={() => {
            toast.info(`Switch your wallet to ${SOLANA_CLUSTER_LABEL[SOLANA_CLUSTER]}`, {
              description:
                "Open your wallet settings → Developer / Network and select the matching network, then reconnect.",
              action: { label: "Reconnect", onClick: () => setConnectOpen(true) },
            });
          }}
          onDisconnect={disconnect}
        />
      )}
      {connectOpen && (
        <ConnectModal
          connecting={connecting}
          error={connectError}
          onClose={() => {
            setConnectOpen(false);
            setConnectError(null);
          }}
          onConnect={connect}
        />
      )}
    </Ctx.Provider>
  );
}

/* ────────────────────────────────────────────────────────────────── */

/** Friendly, dismissible banner shown when wallet network ≠ app network. */
function ClusterMismatchBanner({
  walletCluster,
  onSwitch,
  onDisconnect,
}: {
  walletCluster: SolanaCluster;
  onSwitch: () => void;
  onDisconnect: () => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      role="alert"
      className="fixed inset-x-0 bottom-0 z-[190] flex justify-center px-3 pb-3 sm:bottom-4"
    >
      <div className="flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-amber-400/40 bg-[oklch(0.16_0.05_75_/_0.96)] p-4 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-md sm:flex-row sm:items-center">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-100">Network mismatch</p>
          <p className="mt-0.5 text-xs text-amber-100/80">
            Your wallet is on <strong>{SOLANA_CLUSTER_LABEL[walletCluster]}</strong>, but SFN runs on{" "}
            <strong>{SOLANA_CLUSTER_LABEL[SOLANA_CLUSTER]}</strong>. Balances and transactions may not
            appear correctly.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onSwitch}
            className="rounded-full bg-amber-300 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-obsidian hover:bg-amber-200"
          >
            Switch network
          </button>
          <button
            type="button"
            onClick={onDisconnect}
            className="rounded-full border border-amber-300/40 px-3 py-1.5 text-[11px] font-semibold text-amber-100 hover:bg-amber-300/10"
          >
            Disconnect
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss network warning"
            className="grid h-7 w-7 place-items-center rounded-full text-amber-100/70 hover:text-amber-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function ConnectModal({
  connecting,
  error,
  onClose,
  onConnect,
}: {
  connecting: boolean;
  error: string | null;
  onClose: () => void;
  onConnect: (p: Provider) => Promise<void>;
}) {
  const wallets: { id: Provider; label: string; tag: string }[] = [
    { id: "phantom", label: "Phantom", tag: "Solana · Popular" },
    { id: "solflare", label: "Solflare", tag: "Solana · Native" },
    { id: "backpack", label: "Backpack", tag: "Solana · xNFT" },
  ];
  return (
    <div
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Connect wallet"
        className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-6"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl"
          style={{ background: "oklch(0.97 0.13 120 / 0.18)" }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-display text-xl font-semibold tracking-tight text-foreground">
          Connect wallet
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Approve the request in your Solana wallet. SFN only reads your public key —
          no transaction is broadcast.
        </p>
        {error && (
          <p role="alert" className="mt-3 rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-[11px] text-red-300">
            {error}
          </p>
        )}
        <div className="mt-5 space-y-2">
          {wallets.map((w) => {
            const installed = !!getInjectedProvider(w.id);
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => onConnect(w.id)}
                disabled={connecting}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-[oklch(0.97_0.13_120)]/40 hover:bg-white/[0.06] disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-[oklch(0.97_0.13_120)]" />
                  <span>
                    <span className="block font-display text-sm font-semibold text-foreground">
                      {w.label}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">{w.tag}</span>
                  </span>
                </span>
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                    {installed ? "Detected" : "Install"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
          {`Solana ${SOLANA_CLUSTER_LABEL[SOLANA_CLUSTER]} · read-only public key access.`}
        </p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Formatting helpers (shared)                                        */
/* ────────────────────────────────────────────────────────────────── */

export function formatUsd(n: number, digits = 0) {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: digits })}`;
}
export function formatThb(usd: number) {
  return `THB ${Math.round(usd * THB_PER_USD).toLocaleString("en-US")}`;
}
export function shortAddress(a: string | null) {
  if (!a) return "";
  if (a.includes("…")) return a;
  return a.length > 10 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a;
}
